// src/services/proyectos.ts
/**
 * Service para operaciones de Proyectos.
 */
import { api } from './api';
import type {
  Proyecto,
  ProyectoListItem,
  ProyectoCreate,
  ProyectoUpdate,
  ProyectoFiltros,
  ProyectoFase,
  ProyectoFaseCreate,
  ProyectoFaseUpdate,
  ProyectoSprint,
  ProyectoSprintCreate,
  ProyectoSprintUpdate,
  ProyectoMiembro,
  ProyectoMiembroCreate,
  ProyectoMiembroUpdate,
  ProyectoResumen,
  ProjectStatus,
} from '@/types';

export const proyectosService = {
  // ==================== PROYECTOS ====================
  list: (params?: ProyectoFiltros) => {
    const searchParams = new URLSearchParams();
    if (params?.estado) searchParams.set('estado', params.estado);
    if (params?.cliente_id) searchParams.set('cliente_id', params.cliente_id);
    if (params?.responsable_id) searchParams.set('responsable_id', params.responsable_id);
    if (params?.fecha_desde) searchParams.set('fecha_desde', params.fecha_desde);
    if (params?.fecha_hasta) searchParams.set('fecha_hasta', params.fecha_hasta);
    if (params?.solo_activos !== undefined) searchParams.set('solo_activos', String(params.solo_activos));

    const query = searchParams.toString();
    return api.get<ProyectoListItem[]>(`/proyectos${query ? `?${query}` : ''}`);
  },

  get: (id: string) => api.get<Proyecto>(`/proyectos/${id}`),

  create: (data: ProyectoCreate) => api.post<Proyecto>('/proyectos', data),

  update: (id: string, data: ProyectoUpdate) =>
    api.patch<Proyecto>(`/proyectos/${id}`, data),

  delete: (id: string) => api.delete<void>(`/proyectos/${id}`),

  cambiarEstado: (id: string, estado: ProjectStatus) =>
    api.post<Proyecto>(`/proyectos/${id}/estado?estado=${estado}`),

  getResumen: (id: string) => api.get<ProyectoResumen>(`/proyectos/${id}/resumen`),

  // ==================== FASES ====================
  getFases: (proyectoId: string) =>
    api.get<ProyectoFase[]>(`/proyectos/${proyectoId}/fases`),

  getFase: (faseId: string) =>
    api.get<ProyectoFase>(`/proyectos/fases/${faseId}`),

  createFase: (proyectoId: string, data: ProyectoFaseCreate) =>
    api.post<ProyectoFase>(`/proyectos/${proyectoId}/fases`, data),

  updateFase: (faseId: string, data: ProyectoFaseUpdate) =>
    api.patch<ProyectoFase>(`/proyectos/fases/${faseId}`, data),

  deleteFase: (faseId: string) =>
    api.delete<void>(`/proyectos/fases/${faseId}`),

  reordenarFases: (proyectoId: string, orden: string[]) =>
    api.post<ProyectoFase[]>(`/proyectos/${proyectoId}/fases/reordenar`, orden),

  // ==================== SPRINTS ====================
  getSprints: (proyectoId: string) =>
    api.get<ProyectoSprint[]>(`/proyectos/${proyectoId}/sprints`),

  getSprint: (sprintId: string) =>
    api.get<ProyectoSprint>(`/proyectos/sprints/${sprintId}`),

  getSprintActivo: (proyectoId: string) =>
    api.get<ProyectoSprint | null>(`/proyectos/${proyectoId}/sprint-activo`),

  createSprint: (proyectoId: string, data: ProyectoSprintCreate) =>
    api.post<ProyectoSprint>(`/proyectos/${proyectoId}/sprints`, data),

  updateSprint: (sprintId: string, data: ProyectoSprintUpdate) =>
    api.patch<ProyectoSprint>(`/proyectos/sprints/${sprintId}`, data),

  deleteSprint: (sprintId: string) =>
    api.delete<void>(`/proyectos/sprints/${sprintId}`),

  activarSprint: (sprintId: string) =>
    api.post<ProyectoSprint>(`/proyectos/sprints/${sprintId}/activar`),

  completarSprint: (sprintId: string) =>
    api.post<ProyectoSprint>(`/proyectos/sprints/${sprintId}/completar`),

  // ==================== MIEMBROS ====================
  getMiembros: (proyectoId: string) =>
    api.get<ProyectoMiembro[]>(`/proyectos/${proyectoId}/miembros`),

  addMiembro: (proyectoId: string, data: ProyectoMiembroCreate) =>
    api.post<ProyectoMiembro>(`/proyectos/${proyectoId}/miembros`, data),

  updateMiembroRol: (proyectoId: string, usuarioId: string, data: ProyectoMiembroUpdate) =>
    api.patch<ProyectoMiembro>(`/proyectos/${proyectoId}/miembros/${usuarioId}`, data),

  removeMiembro: (proyectoId: string, usuarioId: string) =>
    api.delete<void>(`/proyectos/${proyectoId}/miembros/${usuarioId}`),
};
